/**
 * This file contains script tag generators for third-party integration scripts used in layouts and pages.
 *
 * For any other integrations NOT managed here, refer to our GTM workspace and documentation.
 *
 * Requirements:
 * 1. All injected third-party integration scripts should be loaded asynchronously (via async or defer attribute) to ensure they
 *    do not block the parsing/rendering of the page. Use `injectClientSideScripts()` inside a SFC's onMounted() hook.
 * 2. All scripts must have a unique 'id' attribute to avoid duplication.
 * 3. These scripts should only be included in the client-side rendering context and not served by the server to avoid
 *    unnecessary server-side processing and potential security risks. Carefully consider how to expose any required data.
 */

interface ScriptAttributes {
  id: string
  src: string
  defer?: boolean
  async?: boolean
  [key: string]: string | boolean | undefined
}

interface HeapConfig {
  disableTextCapture?: boolean
  eventPropertiesStorage?: 'cookies' | 'localstorage'
  metadataStorage?: 'cookies' | 'localstorage'
  logLevel?: 'none' | 'trace'
  enableSecureCookies?: boolean
  ingestServer?: string
  disableSessionReplay?: boolean
  clearEventPropertiesOnNewUser?: boolean
  customEvents?: string[]
  disableInteractionEvents?: ('click' | 'submit' | 'change')[]
  salesforceAura?: boolean
  disablePageviewAutocapture?: boolean
  compressCookies?: boolean
  supportedDomains?: string[]
}

// Extend Window interface to include Heap.io properties
declare global {
  interface Window {
    heap: any
    heapReadyCb: any[]
  }
}

/**
 * Injects client-side scripts into the document head. Ignores scripts that already exist in the document by id.
 *
 * @param {ScriptAttributes[]} scripts - An array of script objects to be injected. Each object represents a script element's attributes.
 * @returns {void}
 */
export const injectClientSideScripts = (scripts: ScriptAttributes[]): void => {
  if (!import.meta.client) return

  const fragment: DocumentFragment = document.createDocumentFragment()

  scripts.forEach((script: ScriptAttributes) => {
    // Do NOT inject the script if there is already a script element with the same id in the document
    if (script.id && document.getElementById(script.id)) {
      return
    }

    const scriptElement: HTMLScriptElement = document.createElement('script')
    Object.entries(script).forEach(([key, value]) => {
      if ((key === 'defer' || key === 'async') && value === true) {
        scriptElement.setAttribute(key, '')
      } else {
        scriptElement.setAttribute(key, String(value))
      }
    })
    fragment.appendChild(scriptElement)
  })

  if (fragment.hasChildNodes()) {
    document.head.appendChild(fragment)
  }
}

export const generateOptimizelyEntryScript = (): ScriptAttributes => {
  const { $i18n, $config } = useNuxtApp()
  let optimizelyAccountID: string

  switch ($i18n.locale.value) {
    case 'en-NZ':
      optimizelyAccountID = $config.public.optimizelyIdNZ
      break
    case 'fr':
    case 'en-CA':
      optimizelyAccountID = $config.public.optimizelyIdCA
      break
    case 'en-AU':
    default:
      optimizelyAccountID = $config.public.optimizelyIdAU
  }

  return {
    id: 'optimizely-script',
    src: `https://cdn.optimizely.com/js/${optimizelyAccountID}.js`,
    type: 'text/javascript',
    async: true, // Execute soonest after download to minimise DOM flashing for A/B tests
  }
}

export const generateSaleFinderScript = (): ScriptAttributes => {
  const { $i18n, $config } = useNuxtApp()
  let saleFinderUrl: string

  switch ($i18n.locale.value) {
    case 'en-NZ':
      saleFinderUrl = $config.public.salefinderUrlNZ
      break
    case 'fr':
    case 'en-CA':
      saleFinderUrl = $config.public.salefinderUrlCA
      break
    case 'en-AU':
    default:
      saleFinderUrl = $config.public.salefinderUrlAU
  }

  return {
    id: 'salefinder-script',
    src: saleFinderUrl,
    type: 'text/javascript',
    defer: true,
  }
}

export const generateBarillianceScript = (): ScriptAttributes => ({
  id: 'barilliance-script',
  src: 'https://cloudfront.barilliance.com/michaelhill.com.au/cbar.js.php',
  type: 'text/javascript',
  defer: true,
})

export const generateTradeDeskScript = (): ScriptAttributes => ({
  id: 'trade-desk-script',
  src: 'https://js.adsrvr.org/up_loader.1.1.0.js',
  type: 'text/javascript',
  defer: true,
})

// Track if Brauz service has been initialized to prevent duplicate initializations
let isBrauzInitialized = false

export const isBrauzServiceInitialized = (): boolean => {
  return isBrauzInitialized
}

export const setBrauzServiceInitialized = (): void => {
  isBrauzInitialized = true
}

export const generateReserveInStoreScript = (): ScriptAttributes => {
  const { locale } = useNuxtApp().$i18n
  const { mhjEnvironment } = useSiteUrl()
  const devOrProd = mhjEnvironment !== 'prod' ? 'dev' : 'prod'
  let reserveInStoreUrl: string

  switch (locale.value) {
    case 'en-NZ':
      reserveInStoreUrl = `https://d3aq2u4yw77ivo.cloudfront.net/reserve-in-store/michael-hill/3.0/reserve-in-store.michaelhill.nz.${devOrProd}.js`
      break
    case 'en-CA':
    case 'fr':
      reserveInStoreUrl = `https://d3aq2u4yw77ivo.cloudfront.net/reserve-in-store/michael-hill/3.0/reserve-in-store.michaelhill.ca.${devOrProd}.js`
      break
    default:
      reserveInStoreUrl = `https://d3aq2u4yw77ivo.cloudfront.net/reserve-in-store/michael-hill/3.0/reserve-in-store.michaelhill.au.${devOrProd}.js`
  }

  return {
    id: 'reserve-in-store-script',
    src: reserveInStoreUrl,
    type: 'text/javascript',
    defer: true,
    onload: 'window.dispatchEvent(new Event("integrationScriptLoaded:reserveInStore"))',
  }
}

/**
 * Setup the Heap.io analytics Web SDK
 *
 * Key notes on this third-party script integration:
 * - This is a heavily-modified entry script from the reference 'install' script snippet Heap.io provide in https://developers.heap.io/docs/web#base-installation
 * - Exposes the heap object globally for use across the application per reference
 * - Enables secureCookies by default for enhanced security
 * - Sets logLevel to 'trace' in development environment for easier debugging (i.e. local dev debugging)
 *
 * @param {string} [appIdOverride] - Optional override for the Heap.io App ID (default to env var)
 * @param {HeapConfig} [configOverride] - Optional override for Heap configuration object
 * @returns {void}
 */
export const setupHeapAnalytics = (appIdOverride?: string, configOverride?: HeapConfig): void => {
  if (!import.meta.client) return
  if (window.heap && window.heap.loaded) return

  try {
    const { public: publicConfig } = useRuntimeConfig()
    // Ensure the App ID is always a string to avoid Heap load call failing with a numeric ID
    const appId = appIdOverride || (publicConfig.heapAppId ? String(publicConfig.heapAppId) : undefined)

    // Verbose Heap logging is a dev-only aid; production must not set logLevel at all.
    const devLogging: Pick<HeapConfig, 'logLevel'> | Record<string, never> = import.meta.dev
      ? { logLevel: 'trace' }
      : {}

    const config: HeapConfig = {
      enableSecureCookies: true,
      ...devLogging,
      ...configOverride,
    }

    // Initialize heap arrays
    window.heapReadyCb = window.heapReadyCb || []
    window.heap = window.heap || []

    // Unlike Heap reference script, do not attach load function to window object
    const load = (envId: string, clientConfig?: HeapConfig): void => {
      window.heap.envId = envId
      window.heap.clientConfig = clientConfig || {}
      window.heap.clientConfig.shouldFetchServerConfig = false
      window.heap.loaded = true

      // Create and load the config script
      const script = document.createElement('script')
      script.type = 'text/javascript'
      script.async = true
      script.src = `https://cdn.us.heap-api.com/config/${envId}/heap_config.js`
      script.onerror = (err) => {
        console.warn('[Heap Analytics] Failed to load script:', err)
      }

      // The stubs below queue calls until it runs, so the SDK can wait for an idle main thread.
      onNuxtReady(() => document.head.appendChild(script))

      // Define API methods that queue calls until SDK is fully loaded
      const methods = [
        'init',
        'startTracking',
        'stopTracking',
        'track',
        'resetIdentity',
        'identify',
        'identifyHashed',
        'getSessionId',
        'getUserId',
        'getIdentity',
        'addUserProperties',
        'addEventProperties',
        'removeEventProperty',
        'clearEventProperties',
        'addAccountProperties',
        'addAdapter',
        'addTransformer',
        'addTransformerFn',
        'onReady',
        'addPageviewProperties',
        'removePageviewProperty',
        'clearPageviewProperties',
        'trackPageview',
      ]

      // Create stub functions that queue method calls
      const createStubFunction =
        (methodName: string) =>
        (...args: any[]) => {
          window.heapReadyCb.push({
            name: methodName,
            fn() {
              if (window.heap[methodName]) {
                window.heap[methodName](...args)
              }
            },
          })
        }

      // Assign stub functions to the heap object
      methods.forEach((methodName) => {
        window.heap[methodName] = createStubFunction(methodName)
      })
    }

    if (appId) {
      load(appId, config)
    }
  } catch (error) {
    console.warn('[Heap Analytics] Failed to initialize:', error)
  }
}

export const generateVirtualTryOnScript = () => {
  const { mhjEnvironment } = useSiteUrl()

  return {
    id: 'vto-script',
    src:
      mhjEnvironment === 'prod'
        ? 'https://inova-base.vto.jewelry/?d=GWZIKT8'
        : 'https://stag2024-Base.paasweb.co.il/?d=GWZIKT8',
    type: 'text/javascript',
    async: true,
  }
}
